/**
 * pdfReport — pdfmake 0.3.x wrapper with Sarabun Thai font
 *
 * pdfmake 0.3.x API:
 *   - addVirtualFileSystem(vfs)  ← register fonts
 *   - addFonts({...})            ← map font names
 *   - createPdf(docDef) returns: { getBlob(), download(), open(), print() }
 *   - NO getDataUrl() — use getBlob() + URL.createObjectURL() instead
 */

import pdfMake from 'pdfmake/build/pdfmake';
import sarabunVfs from '@/utils/pdfFonts';

// Register Sarabun font
pdfMake.addVirtualFileSystem(sarabunVfs);
pdfMake.addFonts({
  Sarabun: {
    normal:      'Sarabun-Regular.ttf',
    bold:        'Sarabun-Bold.ttf',
    italics:     'Sarabun-Italic.ttf',
    bolditalics: 'Sarabun-Bold.ttf',
  },
});

/**
 * Build pdfmake doc definition with default Sarabun styling.
 * Returns the raw pdfmake object (.download(), .open(), .print(), .getBlob()).
 */
export function createReportPdf(docDefinition) {
  const defaults = {
    defaultStyle: {
      font: 'Sarabun',
      fontSize: 10,
    },
    styles: {
      reportTitle: { fontSize: 16, bold: true, margin: [0, 0, 0, 4] },
      reportSubtitle: { fontSize: 10, color: '#6b7280', margin: [0, 0, 0, 16] },
      sectionTitle: { fontSize: 12, bold: true, margin: [0, 16, 0, 8] },
      tableHeader: { bold: true, fontSize: 9, color: '#374151', fillColor: '#f3f4f6' },
      tableCell: { fontSize: 9 },
      kpiLabel: { fontSize: 9, color: '#6b7280' },
      kpiValue: { fontSize: 14, bold: true },
      footerText: { fontSize: 8, color: '#9ca3af', alignment: 'center' },
      ...docDefinition.styles,
    },
    pageSize: 'A4',
    pageMargins: [40, 60, 40, 50],
    ...docDefinition,
  };

  if (!defaults.header) {
    defaults.header = (currentPage, pageCount) => ({
      columns: [
        // { text: 'SIVA', style: { font: 'Sarabun', fontSize: 8, color: '#9ca3af' }, margin: [40, 20, 0, 0] },
        { text: `${currentPage}/${pageCount}`, alignment: 'right', style: { font: 'Sarabun', fontSize: 8, color: '#9ca3af' }, margin: [0, 20, 40, 0] },
      ],
    });
  }

  if (!defaults.footer) {
    defaults.footer = () => ({
      text: `Generated on ${new Date().toLocaleString('th-TH')} — SIVA POS`,
      style: 'footerText',
      margin: [40, 10, 40, 0],
    });
  }

  return pdfMake.createPdf(defaults);
}

/**
 * Get a blob URL for previewing PDF in an iframe.
 * pdfmake 0.3.x has no getDataUrl() — use getBlob() instead.
 *
 * @param {object} pdf — result of createReportPdf()
 * @returns {Promise<string>} blob URL for iframe src
 */
export async function getPdfPreviewUrl(pdf) {
  const blob = await pdf.getBlob();
  return URL.createObjectURL(blob);
}

/**
 * Helper: convert ApexCharts instance to base64 PNG for embedding in PDF.
 */
export async function chartToBase64(chartRef) {
  if (!chartRef?.chart) return null;
  try {
    const { imgURI } = await chartRef.chart.dataURI();
    if (!imgURI || imgURI.length < 100) return null;
    return imgURI;
  } catch {
    return null;
  }
}
